import React from 'react'
import { match } from 'ts-pattern'

import MusicOptions from '../../../MusicOptions'
import ItemWrapper from '../ItemWrapper'

import type { MusicOptionActions } from '../../../MusicOptions/_hooks/menu-options'
import type { MusicOptionKey } from '../../../MusicOptions/_types'
import type { MusicOption, MusicOptionsProps } from '../../../MusicOptions'
import type { AudioGlobalMetadata } from '@/hooks/audio/store'
import type { CoverProps } from '@/components/MusicProfile/_components/Cover'

import MusicProfile from '@/components/MusicProfile'
import { useInWhichPage } from '@/hooks/useInWhichPage'
import { useWaveform } from '@/hooks/waveform'

export type MusicMetadataSuccess = {
  status: 'success'
} & AudioGlobalMetadata
export type showType =
  | 'userInfo'
  | 'duration'
  | 'labels'
  | 'error'
  | 'prompt'
  | 'waveform'
  | 'coverInfo'
  | MusicOptionKey
  | { key: MusicOptionKey; iconOnly?: boolean }

export interface MusicItemSuccessProps
  extends MusicOptionActions,
    Pick<CoverProps, 'playStatus'> {
  data: MusicMetadataSuccess
  onAudioPlay?: (playStart?: number) => void
  onClickWaveform?: () => void
  showTypes?: showType[]
  onlyExpand?: boolean
  musicOptionsProps?: Pick<
    MusicOptionsProps,
    'className' | 'iconClassName' | 'btnClassName' | 'dropdownHidden'
  >
}

const MusicItemSuccess: React.FC<MusicItemSuccessProps> = (props) => {
  const {
    data,
    playStatus,
    onAudioPlay,
    onClickWaveform,
    onDeleted,
    showTypes,
    onlyExpand,
    onSelect,
    musicOptionsProps,
  } = props
  const { shouldShowMusicTagLabel } = useInWhichPage()
  const { data: waveformData, loading: waveformLoading } = useWaveform({
    url:
      data.status === 'success' && !!showTypes?.includes('waveform')
        ? data.waveformSrc
        : null,
  })

  function options(): MusicOption[] {
    if (showTypes) {
      return showTypes
        .map((type) => {
          return match(
            type as
              | MusicOptionKey
              | { key: MusicOptionKey; iconOnly?: boolean },
          )
            .with('reuse-prompt', () => ({
              key: 'reuse-prompt',
              iconOnly: true,
            }))
            .with('download', () => ({ key: 'download', iconOnly: true }))
            .with('share', () => ({ key: 'share', iconOnly: true }))
            .with('delete', () => ({ key: 'delete', iconOnly: true }))
            .with('liked', () => ({ key: 'liked', iconOnly: true }))
            .with('select', () => ({ key: 'select', iconOnly: true }))
            .with({ key: 'reuse-prompt', iconOnly: false }, () => ({
              key: 'reuse-prompt',
              iconOnly: false,
            }))
            .with({ key: 'download', iconOnly: false }, () => ({
              key: 'download',
              iconOnly: false,
            }))
            .with({ key: 'share', iconOnly: false }, () => ({
              key: 'share',
              iconOnly: false,
            }))
            .with({ key: 'delete', iconOnly: false }, () => ({
              key: 'delete',
              iconOnly: false,
            }))
            .with({ key: 'liked', iconOnly: false }, () => ({
              key: 'liked',
              iconOnly: false,
            }))
            .otherwise(() => null)
        })
        .filter(Boolean) as MusicOption[]
    } else {
      return [{ key: 'download', iconOnly: true }]
    }
  }

  return (
    <ItemWrapper
      onClick={() => {
        onAudioPlay?.()
      }}
      className='group/music cursor-pointer gap-2 md:gap-6'
    >
      <MusicProfile
        {...(shouldShowMusicTagLabel ? { showTagLabel: true } : {})}
        data={data}
        cover={{ src: data.cover, playStatus, className: 'shrink-0' }}
        showTypes={showTypes || ['labels', 'duration']}
      />
      {/* <Waveform
        audio={{
          url: data.src,
          peaks: waveformData || [],
          duration: data.duration,
          height: 40,
        }}
        loading={waveformLoading}
        className={cls`hidden w-[455px] shrink-0 ${showTypes?.includes('waveform') ? 'lg:block' : ''}`}
        onPlayAudio={(time) => {
          onClickWaveform?.()
          onAudioPlay?.(time)
        }}
      /> */}
      <MusicOptions
        expand={options()}
        data={data}
        onDeleted={onDeleted}
        onlyExpand={onlyExpand}
        onSelect={onSelect}
        {...musicOptionsProps}
      />
    </ItemWrapper>
  )
}

export default MusicItemSuccess
